import type { Feature, Polygon } from 'geojson';
import { useCallback, useEffect, useState } from 'react';

import useBoundingBox from 'components/Map/hooks/useBoundingBox';
import { GEOJSON_PROPERTY } from 'components/Map/hooks/useFeatures/constants';
import useMapContext from 'components/Map/hooks/useMapContext';
import useTranslation from 'components/Map/hooks/useTranslation';

import { DRAWING_MODE } from './constants';
import type { UseBoundary, UseBoundaryProps } from './types';

const useBoundary = ({ isInteractive = false }: UseBoundaryProps = {}): UseBoundary => {
    const { BOUNDARY, NO_ENTRY } = useTranslation();
    const [boundaryPolygon, setBoundaryPolygon] = useState<Feature<Polygon>>();
    const [noEntryPolygons, setNoEntryPolygons] = useState<Feature<Polygon>[]>([]);

    const { features } = useMapContext();

    const { features: drawnFeatures } = features;

    const { removeBoundingBox, upsertBoundingBox } = useBoundingBox();

    useEffect(() => {
        if (boundaryPolygon && !features.getFeatureById(boundaryPolygon.id as string)) {
            removeBoundaryForFeature(boundaryPolygon.id as string);

            return;
        }

        noEntryPolygons
            .filter(polygon => !features.getFeatureById(polygon.id as string))
            .map(polygon => polygon.id as string)
            .forEach(removeBoundaryForFeature);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawnFeatures]);

    const updateBoundingBox = useCallback(
        (feature: Feature) => {
            upsertBoundingBox(feature, isInteractive);
        },
        [upsertBoundingBox, isInteractive]
    );

    const upsertBoundaryPolygon = (polygon?: Feature<Polygon>) => {
        if (polygon && !boundaryPolygon) {
            const noEntryIds = noEntryPolygons.map(polygon => polygon.id);

            polygon.properties = {
                ...polygon.properties,
                [GEOJSON_PROPERTY.LINKED_NO_ENTRY_ID]: noEntryIds
            };
        }
        setBoundaryPolygon(polygon);
    };

    const upsertNoEntryPolygon = (polygon: Feature<Polygon>) => {
        polygon.properties = {
            ...polygon.properties,
            [GEOJSON_PROPERTY.NAME]: polygon.properties?.[GEOJSON_PROPERTY.NAME] || NO_ENTRY,
            [GEOJSON_PROPERTY.IS_ACTIVE_BOUNDARY]: true
        };

        setNoEntryPolygons(prev => {
            const existingIndex = prev.findIndex(
                existingPolygon => existingPolygon.id === polygon.id
            );

            if (existingIndex >= 0) {
                const updated = [...prev];

                updated[existingIndex] = polygon;

                return updated;
            }

            if (boundaryPolygon) {
                const linkedNoEntryIds =
                    boundaryPolygon.properties?.[GEOJSON_PROPERTY.LINKED_NO_ENTRY_ID] || [];

                boundaryPolygon.properties = {
                    ...boundaryPolygon.properties,
                    [GEOJSON_PROPERTY.LINKED_NO_ENTRY_ID]: [...linkedNoEntryIds, polygon.id]
                };

                polygon.properties = {
                    ...polygon.properties,
                    [GEOJSON_PROPERTY.LINKED_BOUNDARY_ID]: boundaryPolygon.id
                };
            }

            return [...prev, polygon];
        });

        updateBoundingBox(polygon);
    };

    const removeBoundaryForFeature = (featureId: string) => {
        if (boundaryPolygon?.id === featureId) {
            upsertBoundaryPolygon(undefined);
            removeBoundingBox(featureId);
        } else {
            setNoEntryPolygons(prev => prev.filter(polygon => polygon.id !== featureId));
            removeBoundingBox(featureId);
        }
    };

    const updateBoundaryForFeature = (feature: Feature<Polygon>) => {
        const isBoundary =
            feature.properties?.[GEOJSON_PROPERTY.BOUNDARY_TYPE] === DRAWING_MODE.APPLY;

        if (isBoundary) {
            feature.properties = {
                ...feature.properties,
                [GEOJSON_PROPERTY.NAME]: feature.properties?.[GEOJSON_PROPERTY.NAME] || BOUNDARY,
                [GEOJSON_PROPERTY.IS_ACTIVE_BOUNDARY]: true
            };

            upsertBoundaryPolygon(feature);

            upsertBoundingBox(feature, isInteractive);
        } else {
            upsertNoEntryPolygon(feature);
        }
    };

    return {
        boundaryPolygon,
        noEntryPolygons,
        removeBoundaryForFeature,
        updateBoundaryForFeature
    };
};

export default useBoundary;
